/**
 * ChallengePanel — How to play, and the honest thesis.
 *
 * Frames the player as the Red Team, states the goal and controls, and makes
 * the structural thesis explicit: the kernel makes honest-to-honest
 * violations unconstructable; you are confined to your own reach. States the
 * anti-drift theorem collision plainly (skill-74).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
export function ChallengePanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="How to Play"
      subtitle="You are the Red Team. Your goal is to breach the honest core — and to discover that you structurally cannot."
      accentColor={panelTokens.accentGold}
    >
      <PanelSurface.ThesisCard
        color={panelTokens.accent}
        glowColor="rgba(244,63,94,0.10)"
        quoteSource="RD-31 Adversarial Multi-Agent Robustness"
        quote={
          <>
            An adversarial agent cannot escalate the violation surface{" "}
            <strong>beyond its own reachable square set</strong>. The honest
            sub-system stays safe by construction, regardless of adversary
            behavior.
          </>
        }
      >
        Most multi-agent safety stories assume every agent is honest. RD-31
        drops that assumption: you control up to{" "}
        <strong className="text-[var(--adv-bright)]">k</strong> of the{" "}
        <strong className="text-[var(--adv-bright)]">n</strong> agents and may
        make them produce anything (unbounded ALLOW). The question is how much
        damage a k-adversary can do. The answer: the honest agents' governed
        interfaces validate at the boundary, so the honest-to-honest forbidden
        square is{" "}
        <strong className="text-[var(--adv-accent)]">
          never constructable
        </strong>
        . You are confined to squares that pass through an agent you
        compromised.
      </PanelSurface.ThesisCard>

      <PanelSurface.SectionHeading>Your goal</PanelSurface.SectionHeading>
      <PanelSurface.Callout title="Breach the honest core (you can't)" tone="warn">
        Try to construct a forbidden cross-agent square between two honest
        agents through their governed interface. The move generator never offers
        it — the composite is structurally inexpressible. The point of the game
        is to feel that wall and read off, in the HUD, exactly how confined your
        reachable surface is.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>Controls</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.StepList
          ariaLabel="How to play steps"
          items={[
            {
              id: "budget",
              title: "Set your budget k",
              description:
                "Choose how many agents you may compromise. k is capped strictly below n, so an honest core always survives.",
            },
            {
              id: "compromise",
              title: "Compromise agents",
              description:
                "Click an agent to flip it from honest (green circle) to adversarial (red diamond). You can compromise at most k.",
            },
            {
              id: "attempt",
              title: "Attempt a square",
              description:
                "Click a board edge to try to construct a forbidden cross-agent square. Solid amber edges are reachable; dashed blue edges are honest interfaces and carry no constructable violation.",
            },
            {
              id: "read",
              title: "Read the HUD",
              description:
                "Watch the realized surface, the reachable surface, and the §4.4 bound it never exceeds. Your score is coverage of that bounded surface — not threats blocked.",
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>The honest thesis</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="Structural thesis"
          items={[
            {
              id: "isolation",
              title: "Honest-agent isolation",
              body: "Two honest agents with a governed interface cannot be tricked into a forbidden cross-agent square through that interface. This rests on RD-05's mechanized compositional-safety, restricted to the honest sub-system.",
              color: panelTokens.honest,
            },
            {
              id: "confinement",
              title: "Violation confinement",
              body: "Every forbidden square you can realize factors through at least one compromised agent. You cannot escalate beyond your own reachable set.",
              color: panelTokens.surfaceBound,
            },
            {
              id: "bound",
              title: "Bounded surface",
              body: "As k grows, your reachable surface grows but stays bounded by k·a³ + k²·a²·A (RD-31 §4.4). The honest core never appears in it.",
              color: panelTokens.accentBlue,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        By construction, as opposed to a runtime guard
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout title="The theorem collision (anti-drift)" tone="info">
        <PanelSurface.Prose className="m-0">
          The structural win here is{" "}
          <strong className="text-[var(--adv-bright)]">
            unconstructability
          </strong>{" "}
          — the honest-to-honest move is absent from the generator. It is NOT
          "the game noticed your attack" (that would be FM-03 Observation ⇒
          Admissibility) and NOT "your label/color unlocked a move" (FM-01 Label
          ⇒ Permission): compromising an agent does not paint a move legal, it
          changes which squares genuinely factor through an adversary. RD-31's
          third k-resilience condition — honest agents validating incoming data
          at the boundary — IS a runtime defense-in-depth layer, shown here as
          opposed to the structural guarantee, never as a substitute for it. If
          the honest-to-honest square could be realized by spending budget or
          relabeling, RD-31's honest-agent isolation lemma (and RD-05's
          compositional-safety beneath it) would be false.
        </PanelSurface.Prose>
      </PanelSurface.Callout>
    </PanelSurface.PanelShell>
  );
}
